import { FREEZER_MAP } from "../constants/PickingConstant";
import { expectedLabelOf, itemWeight, labelMismatch, normaliseLocationCode } from "./pickRoute";

/**
 * The map of the freezer on Vakken en looproute (2026-10-22): the rows as seen from above, a
 * block per stelling, and per stelling its baskets as you stand in front of them. Built from the
 * vak codes - row digit, stelling in two digits, letter (101A) - so the map is whatever the pick
 * route says, and only where a row stands comes from FREEZER_MAP.
 *
 * Pure: the component (warehouse/pick-route/FreezerMap) only draws it.
 */

export const VAK_STATUS = {
  FILLED: "filled",
  EMPTY: "empty",
  // The bordje text names something else than what lies there (pickRoute labelMismatch)
  LABEL: "label",
  // More than one article: a vak holds one article, the VCE and HVO product of one weight
  MIXED: "mixed",
};

const VAK_CODE = /^(\d)(\d{2})([A-Z])$/;

/** "203C" -> { row: 2, stelling: "203", position: 3, letter: "C" }; null for a code of another shape. */
export const parseVakCode = (code) => {
  const match = VAK_CODE.exec(normaliseLocationCode(code || ""));
  return match ? { row: Number(match[1]), stelling: `${match[1]}${match[2]}`, position: Number(match[2]), letter: match[3] } : null;
};

/** What lies in a vak, as one word for the map. */
export const vakStatus = (entry) => {
  const items = ((entry && entry.products) || []).filter((item) => !!item && !!item.productId);
  if (items.length === 0) return VAK_STATUS.EMPTY;
  const perShop = new Map();
  items.forEach((item) => perShop.set(item.shopSource, (perShop.get(item.shopSource) || 0) + 1));
  const weights = new Set(items.map(itemWeight));
  if ([...perShop.values()].some((count) => count > 1) || weights.size > 1) return VAK_STATUS.MIXED;
  return labelMismatch(entry) ? VAK_STATUS.LABEL : VAK_STATUS.FILLED;
};

const letterAt = (index) => String.fromCharCode(65 + index);

/** The worst of a stelling's baskets decides its colour on the map. */
const rackStatus = (counts, slots) => {
  if (counts.mixed > 0) return VAK_STATUS.MIXED;
  if (counts.label > 0) return VAK_STATUS.LABEL;
  if (counts.filled === 0) return VAK_STATUS.EMPTY;
  return counts.filled === slots ? "full" : "partial";
};

/**
 * @returns {{
 *   columns: Array<{ kind: "aisle", aisle, walking } | { kind: "row", row, title, subtitle, letters, racks }>,
 *   outside: Array<entry>, withoutCode: number
 * }} a row's racks from top to bottom as the map shows them (the door is at the bottom); per rack
 *   its baskets A, B, C … up to the highest letter it has, a letter without a vak as `vak: null`
 */
export const buildFreezerMap = (entries, layout = FREEZER_MAP) => {
  const rows = new Set(layout.columns.filter((column) => !!column.row).map((column) => column.row));
  const racks = new Map();
  const outside = [];
  let withoutCode = 0;

  (entries || []).forEach((entry) => {
    if (!entry) return;
    if (!entry.location) {
      if ((entry.products || []).length > 0) withoutCode += 1;
      return;
    }
    const code = parseVakCode(entry.location);
    if (!code || !rows.has(code.row)) {
      outside.push(entry);
      return;
    }
    if (!racks.has(code.stelling)) racks.set(code.stelling, { stelling: code.stelling, row: code.row, position: code.position, vakken: new Map() });
    racks.get(code.stelling).vakken.set(code.letter, {
      code: normaliseLocationCode(entry.location),
      letter: code.letter,
      entry,
      status: vakStatus(entry),
      article: expectedLabelOf(entry),
      label: (entry.label || "").trim(),
      shops: [...new Set((entry.products || []).map((item) => item.shopSource).filter(Boolean))],
    });
  });

  const rackOf = ({ stelling, row, position, vakken }) => {
    const highest = Math.max(...[...vakken.keys()].map((letter) => letter.charCodeAt(0) - 65));
    const slots = Array.from({ length: highest + 1 }, (_, index) => ({ letter: letterAt(index), vak: vakken.get(letterAt(index)) || null }));
    const counts = { filled: 0, empty: 0, label: 0, mixed: 0 };
    slots.forEach(({ vak }) => {
      if (!vak || vak.status === VAK_STATUS.EMPTY) counts.empty += 1;
      else {
        counts.filled += 1;
        if (vak.status === VAK_STATUS.LABEL) counts.label += 1;
        if (vak.status === VAK_STATUS.MIXED) counts.mixed += 1;
      }
    });
    return { stelling, row, position, slots, counts, status: rackStatus(counts, slots.length) };
  };

  const columns = layout.columns.map((column) => {
    if (!column.row) return { kind: "aisle", aisle: column.aisle, walking: column.walking };
    const ofRow = [...racks.values()].filter((rack) => rack.row === column.row).sort((a, b) => a.position - b.position);
    // Numbered from the door ("up"): stelling 01 at the bottom, so the highest number on top
    const ordered = column.numbering === "up" ? ofRow.reverse() : ofRow;
    return { kind: "row", row: column.row, title: column.title, subtitle: column.subtitle, letters: column.letters, racks: ordered.map(rackOf) };
  });

  return { columns, outside, withoutCode };
};

/** The stelling to show first: the first one with something to put right, else the first one. */
export const firstRackToShow = (map) => {
  const all = map.columns.filter((column) => column.kind === "row").flatMap((column) => [...column.racks].sort((a, b) => a.position - b.position));
  const problem = all.find((rack) => rack.status === VAK_STATUS.MIXED || rack.status === VAK_STATUS.LABEL);
  return (problem || all[0] || null)?.stelling || null;
};
